import * as d3 from 'd3';
import { forEach } from 'lodash';
import { COORDINATES, Gradient } from './gradient.model';

export class GaugeChart {
  svg?: any;
  group?: any;
  outerRadius?: any;
  innerRadius: any;
  width: any;
  height: any;
  needle: Needle;
  page: any;
  constructor(props) {
    this.svg = props.svg;
    this.page = props.page;
    this.group = this.svg.append('g');
    this.outerRadius = props.outerRadius;
    this.innerRadius = props.innerRadius;
    this.width = this.outerRadius * 2;
    this.height = this.outerRadius * 1.4;
    this.needle =  new Needle({
      svg: this.svg,
      len: props.page === 'ExternalPage' ? 45 : this.outerRadius * 0.65,
      radius: props.page === 'ExternalPage' ? 6 : this.innerRadius * 0.15,
      x: this.outerRadius,
      y: this.outerRadius,
      page: this.page
    });
  }

  percentToDegree(perc) {
    return perc * 360;
  }

  degreeToRadian(deg) {
    return deg * Math.PI / 180;
  }

  percentToRadian(perc) {
    return this.degreeToRadian(this.percentToDegree(perc));
  }

  render(data?) {
    const numSections = 20;
    const padRad = 0.03;
    const sectionPerc = 1 / numSections / 2;
    const chartInset = this.page === 'ExternalPage' ? 1 : 21;
    let totalPercent = 0.75;
    const margin = { top: 20, right: 20, bottom: 30, left: 20 };
    const radius = Math.max(this.width, this.height) / 2;
    const barWidth = this.page === 'ExternalPage' ? 25 : 35;

    const defs = this.group.append('defs');

    for (let i = 0; i < 20; i++) {
      this.generalGradient1('#cc0000', '#ffc000', i + 1);
      this.generalGradient1('#ffc000', '#247f24', i + 21);
    }

    this.svg
      .attr('width', this.width)
      .attr('height', this.height);

    for (let i = 0; i < numSections; i++) {
      const arcStartRad = this.percentToRadian(totalPercent);
      const arcEndRad = arcStartRad + this.percentToRadian(sectionPerc);
      totalPercent += sectionPerc;

      const startPadRad = i === 0 ? 0 : padRad / 2;
      const endPadRad = i === numSections - 1 ? 0 : padRad / 2;

      const arc = d3.arc()
        .outerRadius(radius - chartInset)
        .innerRadius(radius - chartInset - barWidth)
        .startAngle(arcStartRad + startPadRad)
        .cornerRadius(5)
        .endAngle(arcEndRad - endPadRad);

      this.group
        .append('path')
        .attr('d', arc)
        .attr('transform', `translate(${this.outerRadius},${this.outerRadius})`);
    }

    // this.group
    //   .attr('width', this.width * 3)
    //   .attr('height', this.height * 3);
    if (!data) {
      this.needle.render();
    }
  }

  reFillColor(data) {
    const self = this;
    this.group.selectAll('path')
      .each(function(d, index) {
        const pathEl = d3.select(this);
        pathEl.attr('fill', () => data ? self.calculateColor(index, data, 20) : 'white');
      });
  }

  animateTo(p, v): void {
    this.needle.animateTo(p, v);
  }

  calculateColor = (index: number, data, numSections) => {
    index = index + 1;

    const min = Math.min(data.b, data.e, data.m, data.n);
    const max = Math.max(data.b, data.e, data.m, data.n);
    const value = index / numSections * 100;
    const step = 100 / numSections;

    const red = ((data.b - min) / (max - min)) * 100;
    const yellow = ((data.e - min) / (max - min)) * 100;

    if ((data.b == 0 && data.e == 0) || value <= red) {
        return '#cc0000'; // Red color
    }

    if (value - red < step) {
      return this.getGradientYellow(index); //gradident red/yellow
    }

    if (value <= yellow) {
        return '#ffc000'; // Yellow color
    }

    if (value - yellow < step) {
      return this.getGradientYellow(20 + index); //gradident yellow/green
    }

    return '#247f24'; // Green color
  }

  getGradientYellow(index) {
    return `url(#gradient${index})`;
  }

  generalGradient1(color1, color2, index) {
    const coordinates = COORDINATES;
    const position = index > 20 ? index - 20 : index;
    const gradient = this.group.select('defs')
      .append('linearGradient')
      .attr('id', `gradient${index}`)
      .attr('x1', `${coordinates[position - 1].x1}%`) // start point of x
      .attr('y1', `${coordinates[position - 1].y1}%`) // start point of y
      .attr('x2', `${coordinates[position - 1].x2}%`) // end point of x
      .attr('y2', `${coordinates[position - 1].y2}%`);

    gradient.append('stop')
      .attr('stop-color', color1)
      .attr('offset', '0%')
      .style('stop-opacity', 1);

    gradient.append('stop')
      .attr('stop-color', color2)
      .attr('offset', '100%');
  }
}

export class Needle {
  svg: any;
  group: any;
  len: any;
  radius: any;
  x: any;
  y: any;
  lastP: any;
  page: any;
  constructor(props) {
    this.svg = props.svg;
    this.group = this.svg.append('g');
    this.len = props.len;
    this.radius = props.radius;
    this.x = props.x;
    this.y = props.y;
    this.page = props.page;
  }

  render() {
    this.group.attr('transform', `translate(${this.x},${this.y})`);
    this.group
      .style('fill', '#f3eded')
      .style('stroke', '#bfbfbf')
      .append('circle')
      .attr('class', 'needle-center')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', this.radius * 4);

    this.group
      .append('path')
      .attr('class', 'needle')
      .attr('d', this._getPath(0));

    this.group
      .style('stroke', '#bfbfbf')
      .style('fill', '#fffff')
      .append('circle')
      .attr('class', 'needle-center')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', this.radius * 3);

    return this.page === 'ExternalPage' ?
    this.group
    .append('text')
    .text(0)
    .style('fill', '#247f24')
    .style('stroke', 'none')
    .style('font-size', '13px')
    .style('font-weight', '700')
    .style('text-anchor', 'middle')
    .attr('transform', `translate(0, 5)`)
    : this.group
      .append('text')
      .text(0)
      .style('fill', '#247f24')
      .style('stroke', 'none')
      .style('font-size', '26px')
      .style('font-weight', '700')
      .style('text-anchor', 'middle')
      .attr('transform', `translate(0, 10)`);
  }
  percentToDegree = p => p * 360;

  degreeToRadian = d => d * Math.PI / 180;

  percentToRadian = p => this.degreeToRadian(this.percentToDegree(p));

  animateTo(p, d) {
    this.group
      .transition()
      .delay(500)
      .ease(d3.easeElasticOut)
      .duration(3000)
      .select('path')
      .tween('progress', () => {
        const self = this;
        const lastP = this.lastP || 0;
        return function (step) {
          const progress = lastP + step * (p - lastP);
          d3.select(this)
            .attr('d', self._getPath(progress));
        };
      })
      .on('end', () => this.lastP = p);
    this.group
      .select('text')
      .style('fill', this.getTextColor(d.color))
      .text(d.v === 0 ? '' : d.v);
  }

  getTextColor(clr: string) {
    if (clr === 'green') { return '#247f24'; }
    if (clr === 'red') { return '#cc0000'; }
    if (clr === 'orange') { return '#ffc000'; }
    return 'black';
  }

  _getPath(p) {
    const thetaRad = this.percentToRadian(p / 2);
    const centerX = 0;
    const centerY = 0;
    const topX = centerX - this.len * Math.cos(thetaRad);
    const topY = centerY - this.len * Math.sin(thetaRad);
    const leftX = centerX - this.radius * Math.cos(thetaRad - Math.PI / 2);
    const leftY = centerY - this.radius * Math.sin(thetaRad - Math.PI / 2);
    const rightX = centerX - this.radius * Math.cos(thetaRad + Math.PI / 2);
    const rightY = centerY - this.radius * Math.sin(thetaRad + Math.PI / 2);

    return `M ${leftX} ${leftY} L ${topX} ${topY} L ${rightX} ${rightY}`;
  }
}
